import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Package, Plus, Trash2, AlertTriangle, Calendar, X, Search, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { FeatureGate } from '@/components/features/FeatureGate';
import { PantryLockedPage } from '@/components/features/FeatureLockedPage';
import { Feature } from '@/types/auth';

interface PantryItem {
    id: string;
    ingredientId: string;
    ingredient: {
        id: string;
        name: string;
        category: string;
    };
    amount: number;
    unit: string;
    purchaseDate: string | null;
    expirationDate: string | null;
    location: string | null;
    isExpired: boolean;
    isLow: boolean;
    createdAt: string;
}

const PantryContent = () => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterExpired, setFilterExpired] = useState(false);
    const [newItem, setNewItem] = useState({
        ingredientName: '',
        amount: '',
        unit: 'piece',
        location: 'Pantry',
    });

    const queryClient = useQueryClient();

    // Fetch pantry items
    const {
        data: items = [],
        isLoading,
        error,
    } = useQuery<PantryItem[]>({
        queryKey: ['pantry', filterExpired],
        queryFn: async () => {
            const params = new URLSearchParams();
            // Backend expects 'expired' param, not 'isExpired'
            if (filterExpired) params.append('expired', 'false');
            const response = await api.get(`/pantry?${params.toString()}`);

            // Backend returns { pantryItems: [...], categorized: {...}, pagination: {...} }
            return response.data?.pantryItems || [];
        },
    });

    // Fetch ingredients for autocomplete (simplified - could be enhanced)
    const { data: ingredients = [] } = useQuery({
        queryKey: ['ingredients-search', searchTerm],
        queryFn: async () => {
            if (!searchTerm || searchTerm.length < 2) return [];
            const response = await api.get(`/ingredients?search=${searchTerm}`);
            return response.data;
        },
        enabled: searchTerm.length >= 2,
    });

    // Add item mutation
    const addMutation = useMutation({
        mutationFn: async (item: typeof newItem) => {
            // First, search for existing ingredient
            const searchResponse = await api.get(
                `/ingredients/search?name=${encodeURIComponent(item.ingredientName)}&limit=1`,
            );

            let ingredientId: string;

            if (searchResponse.data && searchResponse.data.length > 0) {
                // Use existing ingredient
                ingredientId = searchResponse.data[0].id;
            } else {
                // Create new ingredient
                const ingredientResponse = await api.post('/ingredients', {
                    name: item.ingredientName,
                    category: 'OTHER', // Default category (must match IngredientCategory enum)
                });
                ingredientId = ingredientResponse.data.id;
            }

            // Then add to pantry
            await api.post('/pantry', {
                ingredientId,
                amount: parseFloat(item.amount),
                unit: item.unit,
                location: item.location,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pantry'] });
            toast.success('Item added to pantry');
            setShowAddForm(false);
            setNewItem({
                ingredientName: '',
                amount: '',
                unit: 'piece',
                location: 'Pantry',
            });
        },
        onError: () => {
            toast.error('Failed to add item');
        },
    });

    // Delete item mutation
    const deleteMutation = useMutation({
        mutationFn: async (itemId: string) => {
            await api.delete(`/pantry/${itemId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pantry'] });
            toast.success('Item removed from pantry');
        },
        onError: () => {
            toast.error('Failed to remove item');
        },
    });

    // Consume item mutation (reduce quantity)
    const consumeMutation = useMutation({
        mutationFn: async ({ itemId, amount }: { itemId: string; amount: number }) => {
            await api.post(`/pantry/${itemId}/consume`, { amount });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pantry'] });
            toast.success('Item quantity updated');
        },
    });

    const handleAddItem = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newItem.ingredientName || !newItem.amount) {
            toast.error('Please fill in all required fields');
            return;
        }
        addMutation.mutate(newItem);
    };

    const handleDeleteItem = (itemId: string, itemName: string) => {
        if (window.confirm(`Remove ${itemName} from pantry?`)) {
            deleteMutation.mutate(itemId);
        }
    };

    // Group items by location
    const groupedItems = items.reduce(
        (acc, item) => {
            const location = item.location || 'Other';
            if (!acc[location]) {
                acc[location] = [];
            }
            acc[location].push(item);
            return acc;
        },
        {} as Record<string, PantryItem[]>,
    );

    const filteredItems = Object.entries(groupedItems).reduce(
        (acc, [location, locationItems]) => {
            const filtered = locationItems.filter((item) =>
                item.ingredient.name.toLowerCase().includes(searchTerm.toLowerCase()),
            );
            if (filtered.length > 0) {
                acc[location] = filtered;
            }
            return acc;
        },
        {} as Record<string, PantryItem[]>,
    );

    if (isLoading) {
        return (
            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading pantry...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center">
                            <Package className="w-10 h-10 mr-3 text-emerald-600" />
                            My Pantry
                        </h1>
                        <p className="text-gray-600 text-lg">
                            Track your ingredients and reduce food waste
                        </p>
                    </div>
                    <Button
                        onClick={() => setShowAddForm(!showAddForm)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                        {showAddForm ? (
                            <X className="w-5 h-5 mr-2" />
                        ) : (
                            <Plus className="w-5 h-5 mr-2" />
                        )}
                        {showAddForm ? 'Cancel' : 'Add Item'}
                    </Button>
                </div>
            </div>

            {/* Add Item Form */}
            {showAddForm && (
                <Card className="p-6 mb-6">
                    <h3 className="text-lg font-semibold mb-4">Add Item to Pantry</h3>
                    <form
                        onSubmit={handleAddItem}
                        className="grid grid-cols-1 md:grid-cols-4 gap-4"
                    >
                        <div>
                            <Label htmlFor="ingredientName">Ingredient Name *</Label>
                            <Input
                                id="ingredientName"
                                type="text"
                                value={newItem.ingredientName}
                                onChange={(e) =>
                                    setNewItem({ ...newItem, ingredientName: e.target.value })
                                }
                                placeholder="e.g., Flour"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="amount">Amount *</Label>
                            <Input
                                id="amount"
                                type="number"
                                step="0.01"
                                value={newItem.amount}
                                onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
                                placeholder="e.g., 2"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="unit">Unit</Label>
                            <select
                                id="unit"
                                value={newItem.unit}
                                onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            >
                                <option value="piece">Piece</option>
                                <option value="cup">Cup</option>
                                <option value="oz">Ounce</option>
                                <option value="lb">Pound</option>
                                <option value="g">Gram</option>
                                <option value="kg">Kilogram</option>
                                <option value="ml">Milliliter</option>
                                <option value="l">Liter</option>
                                <option value="tbsp">Tablespoon</option>
                                <option value="tsp">Teaspoon</option>
                            </select>
                        </div>
                        <div>
                            <Label htmlFor="location">Location</Label>
                            <select
                                id="location"
                                value={newItem.location}
                                onChange={(e) =>
                                    setNewItem({ ...newItem, location: e.target.value })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            >
                                <option value="Pantry">Pantry</option>
                                <option value="Fridge">Fridge</option>
                                <option value="Freezer">Freezer</option>
                                <option value="Cupboard">Cupboard</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div className="md:col-span-4">
                            <Button
                                type="submit"
                                disabled={addMutation.isPending}
                                className="w-full md:w-auto"
                            >
                                {addMutation.isPending ? 'Adding...' : 'Add to Pantry'}
                            </Button>
                        </div>
                    </form>
                </Card>
            )}

            {/* Search and Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                        type="text"
                        placeholder="Search pantry items..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <Button
                    variant="outline"
                    onClick={() => setFilterExpired(!filterExpired)}
                    className={filterExpired ? 'bg-emerald-50 border-emerald-300' : ''}
                >
                    <Filter className="w-4 h-4 mr-2" />
                    {filterExpired ? 'Show All' : 'Hide Expired'}
                </Button>
            </div>

            {/* Pantry Items Grouped by Location */}
            {Object.keys(filteredItems).length === 0 ? (
                <Card className="p-12 text-center">
                    <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                        Your Pantry is Empty
                    </h3>
                    <p className="text-gray-500 mb-4">
                        Add ingredients to track what you have and avoid buying duplicates
                    </p>
                    <Button
                        onClick={() => setShowAddForm(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Add First Item
                    </Button>
                </Card>
            ) : (
                <div className="space-y-6">
                    {Object.entries(filteredItems).map(([location, locationItems]) => (
                        <Card key={location} className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <Package className="w-5 h-5 mr-2 text-emerald-600" />
                                {location}
                                <Badge variant="outline" className="ml-3">
                                    {locationItems.length} items
                                </Badge>
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {locationItems.map((item) => (
                                    <div
                                        key={item.id}
                                        className={`p-4 rounded-lg border ${
                                            item.isExpired
                                                ? 'bg-red-50 border-red-200'
                                                : 'bg-white border-gray-200'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-900">
                                                    {item.ingredient.name}
                                                </h4>
                                                <p className="text-sm text-gray-600">
                                                    {item.amount} {item.unit}
                                                </p>
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    handleDeleteItem(item.id, item.ingredient.name)
                                                }
                                                className="text-red-600 hover:text-red-700"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>

                                        {item.isExpired && (
                                            <div className="flex items-center text-red-600 text-xs mb-2">
                                                <AlertTriangle className="w-3 h-3 mr-1" />
                                                Expired
                                            </div>
                                        )}

                                        {item.isLow && !item.isExpired && (
                                            <div className="flex items-center text-yellow-600 text-xs mb-2">
                                                <AlertTriangle className="w-3 h-3 mr-1" />
                                                Running Low
                                            </div>
                                        )}

                                        {item.expirationDate && (
                                            <div className="text-xs text-gray-500 flex items-center mt-2">
                                                <Calendar className="w-3 h-3 mr-1" />
                                                Expires:{' '}
                                                {new Date(item.expirationDate).toLocaleDateString()}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

const Pantry = () => {
    return (
        <FeatureGate feature={Feature.PANTRY} fallback={<PantryLockedPage />}>
            <PantryContent />
        </FeatureGate>
    );
};

export default Pantry;
